import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { sendEmailTemplateTestRequest } from '@/services/api/emailCenter';
import { F, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Alert, Button, Checkbox, Form, Input, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl, useModel } from 'umi';
import { useEmailCenterValidateMessages } from '../../useValidateMessages';

interface Props {
  uuid: string;
  /** Flat dot-path sample data from the preview, so the mail matches it. */
  variables?: Record<string, string>;
  language?: string;
  /** Offers "send the draft instead" only when there is one. */
  hasDraft?: boolean;
  onClose: () => void;
}

/**
 * Sends the template being edited to an address.
 *
 * Prefilled with the signed-in user's own address, because that is who is
 * clicking: the overwhelmingly common case is "show me this in my inbox", and
 * typing your own address every time is friction for no safety gain.
 *
 * The recipient is still editable — checking how a mail renders in a colleague's
 * client, or in a Gmail vs Outlook account, is the other real use.
 */
const SendTestMailDrawer: React.FC<Props> = ({
  uuid,
  variables,
  language,
  hasDraft,
  onClose,
}) => {
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();
  const { initialState } = useModel('@@initialState');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUserEmail = initialState?.currentUser?.User?.email;

  useEffect(() => {
    form.setFieldsValue({
      to: currentUserEmail ?? '',
      // A draft is the version most worth checking in an inbox — it has not
      // gone out yet, and this is the last look before it does.
      use_draft: Boolean(hasDraft),
    });
  }, [form, currentUserEmail, hasDraft]);

  const handleSend = async () => {
    const values = await form.validateFields();
    setSending(true);
    setError(null);

    const result = await sendEmailTemplateTestRequest({
      params: { uuid },
      payload: {
        to: values.to,
        language,
        variables,
        use_draft: hasDraft ? Boolean(values.use_draft) : false,
      },
    });
    setSending(false);

    processAPIResponseNow(result, {
      showSuccessMessage: true,
      successMessage: getIntlTranslation(intl, {
        id: 'pages.emailCenter.templates.testSent',
        defaultMessage: 'Test email sent.',
      }),
      onSuccess: onClose,
      onError: (data) =>
        setError(
          (data as { message?: string })?.message ??
            getIntlTranslation(intl, {
              id: 'pages.common.serverError',
              defaultMessage: 'There was an error processing your request.',
            }),
        ),
    });
  };

  const submitOnEnter = useSubmitOnEnter(handleSend, { enabled: !sending });

  return (
    <Drawer
      width={520}
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        <F
          id="pages.emailCenter.templates.sendTestTitle"
          defaultMessage="Send a test email"
        />
      }
      footer={
        <Space
          style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          <Button onClick={onClose}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={sending} onClick={handleSend}>
            <F
              id="pages.emailCenter.templates.sendTestAction"
              defaultMessage="Send"
            />
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 12 }}
        />
      )}

      <Form
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        {...submitOnEnter}
      >
        <Form.Item
          name="to"
          label={
            <F
              id="pages.emailCenter.templates.sendTestTo"
              defaultMessage="Send to"
            />
          }
          rules={[{ required: true }, { type: 'email' }]}
        >
          <Input placeholder="you@example.com" autoFocus />
        </Form.Item>

        {hasDraft && (
          <Form.Item name="use_draft" valuePropName="checked">
            <Checkbox>
              <F
                id="pages.emailCenter.templates.sendTestUseDraft"
                defaultMessage="Send the unpublished draft instead of the published version"
              />
            </Checkbox>
          </Form.Item>
        )}
      </Form>

      <p style={{ margin: 0, fontSize: 12, opacity: 0.65 }}>
        <F
          id="pages.emailCenter.templates.sendTestHint"
          defaultMessage="Rendered exactly as a recipient would get it, using the sample data from the preview. The subject is prefixed with [TEST]."
        />
      </p>
    </Drawer>
  );
};

export default SendTestMailDrawer;
